<script setup>
import { computed, onBeforeMount, onBeforeUnmount, ref, watch } from "vue"
import { useHttpErrorsHandler } from "../../Composables/httpErrorsHandler"
import { useLocalStorage } from "../../Composables/useLocalStorage.js"
import { useDataManager } from "../../Composables/useDataManager.js"
import { useProducts } from "../../Composables/useProducts.js"
import { useAuthUserStore } from "../../Store/AuthUserStore.js"
import _ from 'lodash'
import DataTable from "../../Components/DataTable.vue"
import Modal from "../../Components/Modal.vue"

const { httpErrorsHandler } = useHttpErrorsHandler()
const authUserStore = useAuthUserStore()
const authUser = computed(() => authUserStore.authUser)

const storageKey = "Shiftech_ProductList"
const { saveToLocalStorage, loadFromLocalStorage } = useLocalStorage(storageKey)

const {
    url,
    data: products,
    links,
    meta,
    loading,
    query,
    searchLoaded,
    sortingField,
    sortingFieldsOrder,
    changeSorting,
    search,
    loadData,
    showArchived,
    getArchived,
    hideArchived,
} = useDataManager({
    baseUrl: '/api/products',
    baseSearchUrl: '/api/search/products',
    defaultSortingField: 'created_at',
    defaultSortingFieldsOrder: {
        name: 'asc',
        created_at: 'asc',
    },
    onError: (error) => httpErrorsHandler(error),
})

const debouncedSearch = _.debounce(search, 250)

const columns = ref([
    { label: 'Code Produit', field: 'product_code', slot: 'product_code', class: 'col-1' },
    { label: 'Type', slot: 'type', class: 'col-1' },
    { label: 'Nom', field: 'name', sortable: true, class: 'col-6' },
    { label: 'Prix HT', slot: 'price', class: 'col-1 text-end' },
    { label: 'Date création', field: 'created_at', sortable: true, slot: 'created_at', class: 'col-1' },
    { label: 'Détails', slot: 'details', class: 'col-1 text-center' },
    { label: 'Actions', slot: 'actions', class: 'col-1 text-center' },
])

onBeforeMount(() => {
    const savedSettings = loadFromLocalStorage()
    if (savedSettings) {
        url.value = savedSettings.url || url.value
        sortingField.value = savedSettings.sortingField || sortingField.value
        Object.assign(sortingFieldsOrder, savedSettings.sortingFieldsOrder || sortingFieldsOrder)
        query.value = savedSettings.query || ''
        searchLoaded.value = savedSettings.searchLoaded || false
        showArchived.value = savedSettings.showArchived || false
    }

    loadData(url.value)
})

watch(
    [url, sortingField, sortingFieldsOrder, query, searchLoaded, showArchived],
    ([newUrl, newSortingField, newSortingOrder, newQuery, newSearchLoaded, newShowArchived]) => {
        saveToLocalStorage({
            url: newUrl,
            sortingField: newSortingField,
            sortingFieldsOrder: newSortingOrder,
            query: newQuery,
            searchLoaded: newSearchLoaded,
            showArchived: newShowArchived,
        })
    },
    { deep: true }
)

onBeforeUnmount(() => {
    document.querySelectorAll('.modal').forEach(modalElement => {
        const modalInstance = bootstrap.Modal.getInstance(modalElement)
        if (modalInstance) {
            modalInstance.hide()
        }
    })
})

const idToArchive = ref(null)
const productToShow = ref(null)

function showModal(modalId, id = null) {
    if (modalId === 'modalArchive') {
        idToArchive.value = id
    }
    const modale = bootstrap.Modal.getOrCreateInstance(document.getElementById(modalId))
    modale.show()
}

function hideModal(modalId) {
    const modale = bootstrap.Modal.getOrCreateInstance(document.getElementById(modalId))
    modale.hide()
}

function showModalProduct(product) {
    productToShow.value = product
    showModal('modalShowProduct')
}

const {
    archiveProduct,
    restoreProduct,
} = useProducts({
    onArchived: () => {
        loadData(url.value)
        hideModal('modalArchive')
    },
    onRestored: () => {
        loadData(url.value)
    },
    onError: (error) => httpErrorsHandler(error),
})
</script>

<template>
    <div class="row">
        <div class="col-lg-12">
            <div class="card">
                <div class="card-header">
                    <div class="row g-3">
                        <div class="col-md-4">
                            <div class="search-box">
                                <input type="search" class="form-control search" placeholder="Rechercher..." v-model="query" @input="debouncedSearch" id="searchProducts">
                                <i class="ri-search-line search-icon"></i>
                            </div>
                        </div>
                        <div class="col-md-auto ms-auto">
                            <button type="button" class="btn btn-dark waves-light me-2" @click="getArchived" v-if="!showArchived">
                                <i class="ri-archive-fill me-1 align-bottom"></i> Archives
                            </button>
                            <button type="button" class="btn btn-success waves-light me-2" @click="hideArchived" v-else>
                                <i class="mdi mdi-package-variant fs-14 me-1"></i> Produits
                            </button>
                            <router-link :to="{ name:'products.create' }" class="btn btn-secondary" v-if="authUser.can && authUser.can.create_products">
                                <i class="ri-add-fill me-1 align-bottom"></i> Ajouter
                            </router-link>
                        </div>
                    </div>
                </div>
                <div class="card-body">
                    <data-table
                        :columns="columns"
                        :data="products"
                        :loading="loading"
                        :links="links"
                        :meta="meta"
                        :sorting-field="sortingField"
                        :sorting-order="sortingFieldsOrder"
                        :search-loaded="searchLoaded"
                        @sort-changed="changeSorting"
                        @page-changed="loadData"
                    >
                        <!-- Code Produit -->
                        <template #product_code="{ item }">
                            <span class="fw-medium text-dark">{{ item.data.product_code }}</span>
                        </template>

                        <!-- Type -->
                        <template #type="{ item }">
                            <span :class="'badge fs-11 bg-'+item.data.type_badge_color+'-subtle text-'+item.data.type_badge_color">
                                {{ item.data.type_label_fr }}
                            </span>
                        </template>

                        <!-- Prix HT -->
                        <template #price="{ item }">
                            {{ item.data.price_excluding_tax_formatted }}
                        </template>

                        <!-- Date création -->
                        <template #created_at="{ item }">
                            {{ item.data.created_at_fr }}
                        </template>

                        <!-- Détails -->
                        <template #details="{ item }">
                            <a href="#" class="btn btn-secondary bg-gradient waves-effect waves-light btn-sm" @click.prevent="showModalProduct(item)">
                                Accéder
                            </a>
                        </template>

                        <!-- Actions -->
                        <template #actions="{ item }">
                            <!-- UPDATE -->
                            <router-link :to="{ name: 'products.edit', params: { id: item.data.id } }" class="text-info" title="Modifier le produit" v-if="item.can.update">
                                <i class="ri-pencil-fill fs-17 px-1"></i>
                            </router-link>
                            <i class="ri-pencil-fill fs-17 px-1 text-muted" v-else></i>

                            <!-- ARCHIVE -->
                            <template v-if="!showArchived">
                                <a href="#" class="text-danger" title="Archiver le produit" @click.prevent="showModal('modalArchive', item.data.id)" v-if="item.can.delete">
                                    <i class="ri-inbox-archive-fill fs-17 px-1"></i>
                                </a>
                                <i class="ri-inbox-archive-fill fs-17 px-1 text-muted" v-else></i>
                            </template>

                            <!-- RESTORE -->
                            <template v-else>
                                <a href="#" class="text-success" title="Restaurer le produit" @click.prevent="restoreProduct(item.data.id)" v-if="item.can.restore">
                                    <i class="ri-inbox-unarchive-fill fs-17 px-1"></i>
                                </a>
                                <i class="ri-inbox-unarchive-fill fs-17 px-1 text-muted" v-else></i>
                            </template>
                        </template>
                    </data-table>

                    <modal id="modalArchive" title="Archiver le produit ?" btn-class="btn-danger" btn-text="Archiver" @click:button="archiveProduct(idToArchive)">
                        <template #icon>
                            <lord-icon src="https://cdn.lordicon.com/gsqxdxog.json" trigger="loop" colors="primary:#212529,secondary:#f06548" style="width:90px;height:90px"></lord-icon>
                        </template>
                        <template #content>
                            <p class="text-muted fs-14 mb-4 pt-1">Vous êtes sur le point d'archiver un produit. Continuer ?</p>
                        </template>
                    </modal>

                    <!-- Modal show product -->
                    <div class="modal fade zoomIn" id="modalShowProduct" tabindex="-1" aria-hidden="true">
                        <div class="modal-dialog modal-lg">
                            <div class="modal-content" v-if="productToShow !== null">
                                <div class="modal-header">
                                    <h5 class="modal-title">Détails du produit</h5>
                                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                                </div>
                                <div class="modal-body p-4">
                                    <p><span class="fw-bold">Nom :</span> {{ productToShow.data.name }}</p>
                                    <p><span class="fw-bold">Prix HT :</span> {{ productToShow.data.price_excluding_tax_formatted }}</p>
                                    <div>
                                        <span class="fw-bold mb-3">Description :</span><br>
                                        <span v-html="productToShow.data.description !== null ? productToShow.data.description.replace(/\n/g, '<br />') : null"></span>
                                    </div>
                                    <div class="text-end mb-0">
                                        <button class="btn btn-soft-dark fw-medium text-decoration-none shadow-none" data-bs-dismiss="modal">Fermer</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>

</style>
